import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { VIDEO_DATA, APP_CONFIG } from '../constants';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';


const WorkShowcase = memo(() => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [currentVideo, setCurrentVideo] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const videoRefs = useRef([]);
  const autoPlayInterval = useRef(null);

  const videoData = VIDEO_DATA;

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying) {
      autoPlayInterval.current = setInterval(() => {
        setCurrentVideo((prev) => (prev + 1) % videoData.length);
      }, APP_CONFIG.AUTO_PLAY_INTERVAL);
    } else {
      clearInterval(autoPlayInterval.current);
    }

    return () => clearInterval(autoPlayInterval.current);
  }, [isAutoPlaying, videoData.length]);

  // Handle video switching
  useEffect(() => {
    // Pause all videos
    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === currentVideo) {
          video.currentTime = 0;
          video.play().catch((error) => {
            if (process.env.NODE_ENV === 'development') {
              console.error('Video play error:', error);
            }
          });
        } else {
          video.pause();
        }
      }
    });
  }, [currentVideo]);

  // Progress tracking
  useEffect(() => {
    const currentVideoElement = videoRefs.current[currentVideo];
    if (currentVideoElement) {
      const updateProgress = () => {
        const progress = (currentVideoElement.currentTime / currentVideoElement.duration) * 100;
        setVideoProgress(progress || 0);
      };

      currentVideoElement.addEventListener('timeupdate', updateProgress);
      return () => currentVideoElement.removeEventListener('timeupdate', updateProgress);
    }
  }, [currentVideo]);

  const handleVideoSelect = useCallback((index) => {
    setCurrentVideo(index);
  }, []);

  const toggleAutoPlay = useCallback(() => {
    setIsAutoPlaying(!isAutoPlaying);
  }, [isAutoPlaying]);

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Political Campaign Reels</h2> */}
        {/* Header */}
        <motion.h2
            ref={ref}
            className="display-text text-center text-gray-900 mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            Political Campaign
            <span className="text-gradient-political"> Reels</span>
          </motion.h2>

        {/* Mobile Screen Mockup with Reels */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          {/* Content Side */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Campaign Video Content</h3>
              <p className="text-lg text-gray-600 mb-6">
                Professional video content creation for political campaigns, including leader introductions,
                policy explanations, and voter engagement reels.
              </p>

              {/* Reel Navigation */}
              <div className="space-y-4">
                {videoData.map((video, index) => (
                  <button
                    key={video.id}
                    onClick={() => handleVideoSelect(index)}
                    className={`w-full text-left p-4 rounded-lg transition-all duration-300 border-l-4 ${
                      index === currentVideo
                        ? 'bg-blue-50 border-blue-600'
                        : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">{video.title}</div>
                    <div className="text-sm text-gray-600">{video.description}</div>
                  </button>
                ))}
              </div>

              {/* Auto-slide Controls */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">Auto-Play Controls</h4>
                  <button
                    onClick={toggleAutoPlay}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    {isAutoPlaying ? '⏸️ Pause Auto-Play' : '▶️ Start Auto-Play'}
                  </button>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <div className="text-sm text-gray-600 mb-2">Progress</div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${videoProgress}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span>{currentVideo + 1}</span> / <span>{videoData.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Screen Mockup */}
          <div className="flex justify-center">
            <div className="relative w-80 h-[600px] bg-black rounded-[3rem] p-2 shadow-2xl">
              <div className="w-full h-full bg-black rounded-[2.5rem] overflow-hidden relative">
                {/* Status Bar */}
                <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 py-3 text-white text-sm">
                  <span>9:41</span>
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-2 border border-white rounded-sm">
                      <div className="w-3 h-1 bg-white rounded-sm"></div>
                    </div>
                  </div>
                </div>

                {/* Video Content */}
                <div className="pt-8 h-full relative">
                  <div className="w-full h-full relative overflow-hidden">
                    {videoData.map((video, index) => (
                      <div
                        key={video.id}
                        className={`absolute inset-0 transition-opacity duration-500 ${
                          index === currentVideo ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <video
                          ref={(el) => (videoRefs.current[index] = el)}
                          className="w-full h-full object-cover"
                          muted
                          loop
                          playsInline
                          preload="metadata"
                          onLoadStart={() => setIsLoading(true)}
                          onCanPlay={() => setIsLoading(false)}
                          onError={() => setIsLoading(false)}
                        >
                          <source src={video.videoSrc} type="video/mp4" />
                          {/* Fallback content */}
                          <div className={`w-full h-full bg-gradient-to-br ${video.fallbackGradient} flex items-center justify-center`}>
                            <div className="text-white text-center p-8">
                              <div className="text-6xl mb-4">{video.fallbackIcon}</div>
                              <h4 className="text-xl font-semibold mb-2">{video.title}</h4>
                              <p className="text-sm opacity-90">{video.description}</p>
                            </div>
                          </div>
                        </video>
                      </div>
                    ))}

                    {/* Video Loading Indicator */}
                    {isLoading && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                        <div className="text-white text-center">
                          <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full mx-auto mb-2"></div>
                          <p className="text-sm">Loading video...</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

export default WorkShowcase;
